import React, { useEffect, useRef, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { format, parseISO } from 'date-fns';
import CheckSVG from '@birdi/icons/check.svg';
import DownloadSVG from '@birdi/icons/download.svg';
import UploadSVG from '@birdi/icons/upload.svg';
import GcpSVG from '@birdi/icons/gcp.svg';
import TargetSVG from '@birdi/icons/target.svg';
import {
  BirdiTheme,
  breakpoints,
  buttonBlue,
  buttonOutlineBlue,
  fonts,
  forms,
} from '@birdi/theme';
import { stroot, crs as crsAPI } from '@birdi/js-sdk';
import { CRSSelect } from '@birdi/crs-select';
import { normaliseBbox } from '@birdi/utils/src/bbox';
import { useMetaSelector } from '../meta/reducer';
import { UpgradeIndicator } from '@birdi/components-common';
import type { Bbox2d } from '@birdi/utils/src/bbox';
import type { CRS } from '@birdi/types/crs';
import type { Bbox } from '@birdi/types/location';
import { mapSitesAPI } from '@birdi/js-sdk/src/public/map-sites';
import { hasGcp, hasPrism } from './site-features';
import type {
  MapSiteReferenceSet,
  MapSiteReferenceSetKind,
} from '@birdi/js-sdk/src/public/map-sites';
import { invalidateSites, useReferenceSets, useSite } from './queries';
import { ModalShell } from './site-modals';
import {
  cancelButtonCSS,
  confirmFooterCSS,
  infoBannerCSS,
  modalHalfButtonCSS,
  modalInputCSS,
  primaryButtonCSS,
} from './styles';
import { readAPIErrorMessage } from './utils';

/**
 * The CRS search endpoint takes a FLAT `[lng, lat, lng, lat]` bbox in SW,NE
 * order, but a site stores a nested pair in NE,SW order (same shape as
 * locations.bbox). Flatten SW-first and let normaliseBbox fix any inversion —
 * the same conversion the legacy mission CRS select does.
 */
const siteBboxToCrsBbox = (bbox: Bbox | null): Bbox2d | null => {
  if (!bbox?.[0] || !bbox?.[1]) return null;
  const flat = [bbox[1][0], bbox[1][1], bbox[0][0], bbox[0][1]];
  if (flat.some((n) => typeof n !== 'number' || Number.isNaN(n))) return null;
  return normaliseBbox(flat as Bbox2d);
};

/** Whole-world fallback: the CRS search needs *a* bbox, else it returns nothing. */
const WORLD_BBOX: Bbox2d = [-180, -85, 180, 85];

/**
 * Bbox used to rank CRS options: the site's footprint when it has one, else a
 * small box around its centre, else the world (unranked but non-empty, so the
 * picker still offers the curated list).
 */
const crsBboxForSite = (site?: {
  bbox?: unknown;
  center?: [number, number] | null;
}): Bbox2d => {
  const fromBbox = siteBboxToCrsBbox((site?.bbox as Bbox | null) ?? null);
  if (fromBbox) return fromBbox;
  const center = site?.center;
  if (center && typeof center[0] === 'number' && typeof center[1] === 'number') {
    const d = 0.05;
    return normaliseBbox([
      center[0] - d,
      center[1] - d,
      center[0] + d,
      center[1] + d,
    ]);
  }
  return WORLD_BBOX;
};

const KIND_META: Record<
  MapSiteReferenceSetKind,
  {
    title: string;
    short: string;
    Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  }
> = {
  gcp: { title: 'Ground Control Points', short: 'GCP', Icon: GcpSVG },
  prism: { title: 'Prism network', short: 'Prism', Icon: TargetSVG },
};

// Title icon in a gray rounded box (matches the design).
const titleIconBoxCSS = (theme: BirdiTheme) => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 8px;
  background: ${theme.mono10};
  color: ${theme.midnight};
  flex-shrink: 0;
  svg {
    width: 1.3rem;
    height: 1.3rem;
  }
`;

// "V{n} current" pill — lightFlora fill, rounded.
const currentBadgeCSS = (theme: BirdiTheme) => css`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.65rem;
  border-radius: 1rem;
  background: ${theme.lightFlora};
  color: ${theme.flora};
  font-weight: 600;
  font-size: 0.8rem;
  svg {
    width: 0.9em;
    height: 0.9em;
  }
`;

// Full-width separator (bleeds past the card's 1.25rem side padding).
const cardSeparatorCSS = (theme: BirdiTheme) => css`
  border: none;
  border-top: 1px solid ${theme.mono20};
  margin: 1.5rem -1.25rem 1.25rem;
`;

const uploadIconCSS = css`
  display: inline-flex;
  margin-right: 0.4rem;
`;

// --- Upload-set modal -------------------------------------------------------

const modalHintCSS = (theme: BirdiTheme) => css`
  display: block;
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
  color: ${theme.mono50};
`;

// Single-row upload control (matches the credentials "associated document"
// field): a bordered box the height of an input, not a full dropzone.
const uploadBoxBaseCSS = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;
  padding: 0.65rem 0.85rem;
  background: ${theme.mono0};
  border: 1px solid ${theme.mono30};
  border-radius: 6px;
  font-size: 0.95rem;
`;

const uploadEmptyButtonCSS = (theme: BirdiTheme) => css`
  ${uploadBoxBaseCSS(theme)};
  justify-content: center;
  color: ${theme.mono50};
  cursor: pointer;
  svg {
    width: 1.1em;
    height: 1.1em;
  }
  &:hover {
    border-color: ${theme.mono40};
  }
`;

const selectedFileCSS = (theme: BirdiTheme) => css`
  ${uploadBoxBaseCSS(theme)};
  color: ${theme.mono70};
  .name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }
`;

const removeFileButtonCSS = (theme: BirdiTheme) => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  color: ${theme.mono50};
  &:hover {
    color: ${theme.mono70};
  }
`;

const noteLabelCSS = (theme: BirdiTheme) => css`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${theme.mono70};
`;

// CRS picker block: bold label above the select, hint below. Copy is Proxima
// Nova / midnight (the select itself keeps react-select's own typography).
const crsFieldCSS = (theme: BirdiTheme) => css`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 1rem;
  font-family: ${fonts.body};
  color: ${theme.midnight};
  .label {
    font-size: 0.85rem;
    font-weight: 600;
    color: ${theme.midnight};
  }
  .hint {
    font-size: 0.78rem;
    color: ${theme.midnight};
  }
`;

const noteTextareaCSS = (theme: BirdiTheme) => css`
  ${modalInputCSS(theme)};
  min-height: 4.5rem;
  resize: vertical;
  font-weight: normal;
`;

// `forms.errorMessage` carries a -0.8rem top margin, which is there to pull the
// message up under an input that has its own bottom margin. The note textarea
// has none, so the shared default lands the text ON the textarea's border.
// Override just the top margin and keep the rest of the shared style.
const uploadErrorCSS = css`
  margin-top: 0.6rem;
`;

/**
 * Upload-a-reference-set modal: a styled CSV dropzone plus an optional note
 * that becomes the new version's description. Matches the other mini modals
 * (ModalShell + shared footer/button styles).
 */
const UploadReferenceSetModal = ({
  open,
  setOpen,
  siteId,
  kind,
  bbox,
  inheritedSrid,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
  siteId: string;
  kind: MapSiteReferenceSetKind;
  /**
   * Flat SW,NE bbox for the CRS search — ranks the options so the site's own
   * zone is on top. Built by crsBboxForSite (never empty).
   */
  bbox: Bbox2d;
  /** Previous version's CRS, pre-selected so re-uploads inherit it. */
  inheritedSrid: number | null;
}) => {
  const meta = KIND_META[kind];
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [crsValue, setCrsValue] = useState<CRS | null>(null);
  const [inheritedCrs, setInheritedCrs] = useState<CRS | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setNote('');
      setCrsValue(null);
      setSubmitting(false);
      setError(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  // Resolve the previous version's srid to a full CRS so it can be pre-selected
  // (the select needs the whole object, the API only stores the srid).
  useEffect(() => {
    if (!open || !inheritedSrid) {
      setInheritedCrs(null);
      return;
    }
    let cancelled = false;
    crsAPI
      .search(stroot('refcrs'), 'refcrs', { epsgCode: inheritedSrid })
      .then((res) => {
        if (!cancelled) setInheritedCrs(res.body?.[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setInheritedCrs(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, inheritedSrid]);

  const submit = async () => {
    if (!file || !crsValue?.srid) return;
    setSubmitting(true);
    setError(null);
    try {
      const csv = await file.text();
      await mapSitesAPI.uploadReferenceSetVersion(stroot('refup1'), siteId, {
        kind,
        csv,
        srid: crsValue.srid,
        // Kept so the toolbox can show "File reference" — the stored S3 key is
        // a generated path, not the name the user recognises.
        fileName: file.name,
        description: note.trim() || undefined,
      });
      invalidateSites(siteId);
      setOpen(false);
    } catch (err) {
      setError(readAPIErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={`Upload ${meta.short} set`} open={open} setOpen={setOpen}>
      <span css={modalHintCSS}>
        Upload a {meta.short} CSV. Need the format? Download the template from the
        reference set card — it has the columns for this set type.
      </span>
      {file ? (
        <div css={selectedFileCSS}>
          <span className="name">{file.name}</span>
          <button
            type="button"
            aria-label="Remove file"
            css={removeFileButtonCSS}
            data-testid={`ref-set-file-remove-${kind}`}
            onClick={() => {
              setFile(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <button
          type="button"
          css={uploadEmptyButtonCSS}
          data-testid={`ref-set-file-button-${kind}`}
          onClick={() => fileInputRef.current?.click()}
        >
          <span css={uploadIconCSS}>
            <UploadSVG />
          </span>
          Upload CSV file
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        data-testid={`upload-set-file-${kind}`}
        css={css`
          display: none;
        `}
        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
      />
      {/* The CSV's x,y carry no projection of their own, so the CRS has to be
          stated — the same numbers are a valid point in every UTM zone. Options
          are ranked by the site's location, and default to the previous
          version's CRS when there is one. */}
      <div css={crsFieldCSS} data-testid={`ref-set-crs-${kind}`}>
        <span className="label">Coordinate system of the set</span>
        <CRSSelect
          id={`ref-set-crs-select-${kind}`}
          bbox={bbox}
          priorityOnly
          // The template is eastings/northings in metres, so only PROJECTED
          // systems are valid. Without this the list is topped by geographic
          // options (GDA2020 + AHD height), and since the picker auto-selects the
          // first option, uploads silently reprojected metres as if they were
          // degrees.
          crsKind={{ PROJCS: true }}
          defaultCrs={inheritedCrs ?? undefined}
          updateForm={(option: CRS | null) => setCrsValue(option)}
          // deepOcean focus ring on the control (matches the other Sites
          // inputs) — react-select's `primary` drives the focused border.
          selectTheme={(base: any) => ({
            ...base,
            colors: {
              ...base.colors,
              primary: theme.deepOcean,
              primary25: theme.sand,
              primary50: theme.lightOcean,
            },
          })}
          // Default react-select paints the SELECTED option in `primary`
          // (deepOcean) — far too dark to read. Selected = lightOcean,
          // hover = sand, text always midnight.
          styles={{
            option: (base: any, state: any) => ({
              ...base,
              backgroundColor: state.isSelected
                ? theme.lightOcean
                : state.isFocused
                  ? theme.sand
                  : 'transparent',
              color: theme.midnight,
              ':active': { backgroundColor: theme.lightOcean },
            }),
          }}
        />
        <span className="hint">
          {inheritedCrs
            ? `Defaults to the previous version's CRS (${inheritedCrs.coordRefSysName}).`
            : 'Pick the coordinate system the surveyor exported these points in.'}
        </span>
      </div>
      <label css={noteLabelCSS}>
        Note (optional)
        <textarea
          css={noteTextareaCSS}
          placeholder="e.g. Re-surveyed after north bench cut"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          data-testid={`ref-set-note-${kind}`}
        />
      </label>
      {error && (
        <div css={[forms.errorMessage, uploadErrorCSS]}>{error}</div>
      )}
      <footer css={confirmFooterCSS}>
        <button
          type="button"
          css={cancelButtonCSS}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          css={primaryButtonCSS}
          disabled={!file || !crsValue?.srid || submitting}
          data-testid={`upload-set-save-${kind}`}
          onClick={submit}
        >
          {submitting ? 'Uploading…' : 'Upload'}
        </button>
      </footer>
    </ModalShell>
  );
};

const ReferenceSetPanel = ({
  siteId,
  kind,
  set,
  bbox,
  locked = false,
}: {
  siteId: string;
  kind: MapSiteReferenceSetKind;
  set: MapSiteReferenceSet | undefined;
  bbox: Bbox2d;
  /**
   * Not entitled to this kind of set. The panel is still rendered — dimmed,
   * inert and badged — so the customer can see what the plan would give them.
   * Hiding it would just look like the feature doesn't exist.
   */
  locked?: boolean;
}) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const meta = KIND_META[kind];
  const versions = set?.versions ?? [];
  const current =
    versions.find((v) => v.version === set?.currentVersion) ??
    versions[0] ??
    null;

  return (
    <section
      data-testid={`reference-set-panel-${kind}`}
      data-locked={locked || undefined}
      css={(theme) => css`
        flex: 1;
        min-width: 0;
        background: ${theme.mono0};
        border: 1px solid ${theme.mono20};
        border-radius: 10px;
        box-shadow: 0 1px 4px rgba(9, 30, 66, 0.06);
        padding: 1rem 1.25rem;
        ${locked &&
        css`
          opacity: 0.5;
          /* Inert rather than merely faded — a dimmed but clickable Upload
             would 403 at the API and read as a bug. The badge stays clickable
             so its tooltip still works. */
          pointer-events: none;
          user-select: none;
        `}
      `}
    >
      <header
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          flex-wrap: wrap;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 0.75rem;
          `}
        >
          <span css={titleIconBoxCSS}>
            {/* The GCP glyph fills its box more than the Prism target, so nudge
                it down a touch to read as the same visual size. */}
            <meta.Icon
              style={
                kind === 'gcp'
                  ? { width: '1.1rem', height: '1.1rem' }
                  : undefined
              }
            />
          </span>
          <div>
            {/* id is the Intercom anchor — Intercom injects the (i) hint here
                for the Prism card; no rendered icon of our own. */}
            <h3
              id={kind === 'prism' ? 'ref-set-prism-info' : undefined}
              css={css`
                margin: 0;
                display: flex;
                align-items: center;
                gap: 0.4rem;
              `}
            >
              {meta.title}
              {/* pointer-events re-enabled: the section is inert, but the
                  badge's tooltip has to stay reachable. */}
              {locked && (
                <span
                  css={css`
                    pointer-events: auto;
                  `}
                >
                  <UpgradeIndicator
                    message={`Upgrade plan to use ${meta.title.toLowerCase()}`}
                  />
                </span>
              )}
            </h3>
            <span
              css={(theme) => css`
                color: ${theme.mono70};
                font-size: 0.875rem;
              `}
            >
              {current?.pointsCount ?? 0} points · {meta.short}
            </span>
          </div>
        </div>
        {current && (
          <span css={currentBadgeCSS}>
            <CheckSVG />V{current.version} current
          </span>
        )}
      </header>

      <div
        css={css`
          display: flex;
          gap: 0.75rem;
          margin: 1rem 0 0;
        `}
      >
        <button
          type="button"
          css={(theme) => css`
            ${buttonBlue(theme)};
            ${modalHalfButtonCSS};
          `}
          data-testid={`upload-set-${kind}`}
          onClick={() => setUploadOpen(true)}
        >
          <span css={uploadIconCSS}>
            <UploadSVG />
          </span>
          Upload set
        </button>
        <a
          href={mapSitesAPI.referenceSetTemplateUrl(kind)}
          download
          css={(theme) => css`
            ${buttonOutlineBlue(theme)};
            ${modalHalfButtonCSS};
          `}
          data-testid={`download-template-${kind}`}
        >
          <span css={uploadIconCSS}>
            <DownloadSVG />
          </span>
          Download template
        </a>
      </div>
      <UploadReferenceSetModal
        open={uploadOpen}
        setOpen={setUploadOpen}
        siteId={siteId}
        kind={kind}
        bbox={bbox}
        inheritedSrid={current?.srid ?? null}
      />

      <hr css={cardSeparatorCSS} />

      {versions.length === 0 ? (
        <div
          css={(theme) => css`
            text-align: center;
            padding: 1.5rem 0.5rem;
            color: ${theme.mono50};
            p {
              margin: 0.25rem 0;
            }
          `}
        >
          <p
            css={(theme) => css`
              font-weight: bold;
              color: ${theme.mono50};
            `}
          >
            No reference sets available
          </p>
          <p>Upload a {meta.short} file to get started.</p>
        </div>
      ) : (
        <div>
          <h4
            css={css`
              margin: 0 0 1rem;
              font-weight: normal;
            `}
          >
            Version history
          </h4>
          <ol
            css={(theme) => css`
              list-style: none;
              margin: 0;
              padding: 0;
              border-left: 2px solid ${theme.mono20};
            `}
          >
            {versions.map((version, index) => (
              <li
                key={version.id}
                css={(theme) => css`
                  position: relative;
                  display: flex;
                  align-items: flex-start;
                  gap: 1rem;
                  padding: 0 0 1.25rem 1.25rem;
                  &::before {
                    content: '';
                    position: absolute;
                    left: -0.5rem;
                    top: 0.15rem;
                    width: 0.85rem;
                    height: 0.85rem;
                    border-radius: 50%;
                    border: 2px solid ${theme.mono0};
                    background: ${index === 0 ? theme.flora : theme.mono30};
                  }
                `}
              >
                <div
                  css={css`
                    flex: 1;
                    min-width: 0;
                  `}
                >
                  <div
                    css={css`
                      font-weight: bold;
                    `}
                  >
                    v{version.version}
                  </div>
                  {version.description && <div>{version.description}</div>}
                  <div
                    css={(theme) => css`
                      color: ${theme.mono50};
                      font-size: 0.85rem;
                    `}
                  >
                    Referenced by {version.referencedByCount ?? 0} report
                    {(version.referencedByCount ?? 0) === 1 ? '' : 's'}
                  </div>
                  {/* CRS is what makes these coordinates interpretable —
                      show it rather than leaving it implicit. */}
                  {version.srid && (
                    <div
                      css={(theme) => css`
                        color: ${theme.mono50};
                        font-size: 0.8rem;
                      `}
                    >
                      {version.crsName || `EPSG:${version.srid}`}
                    </div>
                  )}
                </div>
                <div
                  css={(theme) => css`
                    color: ${theme.mono50};
                    font-size: 0.85rem;
                    white-space: nowrap;
                    flex-shrink: 0;
                  `}
                >
                  {version.createdAt
                    ? format(parseISO(version.createdAt), 'dd MMM yyyy')
                    : ''}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
};

export const ReferenceSetsTab = ({ siteId }: { siteId: string }) => {
  const meta = useMetaSelector();
  const { data: referenceSets, isLoading } = useReferenceSets(siteId);
  // Site footprint, used to rank the CRS options in the upload modal.
  const { data: site } = useSite(siteId);
  const crsBbox = crsBboxForSite(site);

  if (isLoading) {
    return (
      <div
        css={css`
          padding: 2em;
          text-align: center;
        `}
      >
        Loading ...
      </div>
    );
  }

  const sets = Array.isArray(referenceSets) ? referenceSets : [];
  // GCPs and prisms are separate entitlements; each panel dims on its own.
  const canUseGcp = hasGcp(meta.features);
  const canUsePrisms = hasPrism(meta.features);
  const gcp = sets.find((set) => set.kind === 'gcp');
  const prism = sets.find((set) => set.kind === 'prism');

  return (
    <div>
      <div css={infoBannerCSS}>
        <span>
          <strong>Reference sets</strong> live at the site level and are shared
          across every map here. Updating a set creates a new version that
          supersedes the previous one. Reports resolve whichever version was
          active as of their report date, so older reports stay reproducible.
        </span>
      </div>
      <div
        css={css`
          display: flex;
          gap: 1rem;
          align-items: flex-start;
          @media (max-width: ${breakpoints.xs}) {
            flex-direction: column;
          }
        `}
      >
        {/* Both panels always render. An org that isn't entitled sees the panel
            dimmed with an upgrade badge — the design's treatment — rather than
            an empty tab that looks like the feature doesn't exist. */}
        <ReferenceSetPanel
          siteId={siteId}
          kind="gcp"
          set={gcp}
          bbox={crsBbox}
          locked={!canUseGcp}
        />
        <ReferenceSetPanel
          siteId={siteId}
          kind="prism"
          set={prism}
          bbox={crsBbox}
          locked={!canUsePrisms}
        />
      </div>
    </div>
  );
};
